'use client';

import { useEffect, useRef, useState } from 'react';

import { LottieRef } from '@northladder/design-system';
import { useTranslations } from '@northladder/i18n';
import { ErrorEventsEnum, TTextLocalized } from '@northladder/utilities';
import { logErrorToSentry } from '@northladder/web-utils';

import {
  cleanupAudioResources,
  clearAllTimeouts,
  cleanupMediaRecorder,
  compareAudioWithMFCC,
  createAndResumeAudioContext,
  createRecordedAudioFile,
  detectExternalAudioDevices,
  getSupportedMimeType,
  getWaveformComparisonConstants,
  IExternalDevice,
  initializeAudioContextOnInteraction,
  isMobileDevice,
  preloadAudio,
  resumeAudioContextIfSuspended,
  setupAndPlayAudio,
  setupMediaRecorderErrorHandler,
  startMediaRecorder,
  stopMediaRecorder,
  wait,
} from '../utils';

interface IFeedbackMessage {
  message: TTextLocalized;
  type: 'error' | 'info' | 'success';
}

interface ISoundDetectionStatus {
  status: 'SUCCESS' | 'FAILURE' | null;
}

interface UseSoundPerformanceEvaluationOptions {
  browserNotSupportedErrorMessage?: string;
  detectionErrorMessage?: string;
  externalDeviceDetectedGeneric?: string;
  externalDetectionErrorMessage?: string;
  maxAttempts?: number;
  mobileSimilarityThreshold?: number;
  onFailure?: () => void;
  onSuccess?: () => void;
  referenceAudioPath?: string;
  similarityThreshold?: number;
}

interface UseSoundPerformanceEvaluationReturn {
  animationRef: LottieRef;
  attemptCount: number;
  audioPlaying: boolean;
  audioRef: ReturnType<typeof useRef<HTMLAudioElement | null>>;
  buttonEnabled: boolean;
  cleanupAll: () => void;
  feedbackMessage: IFeedbackMessage | null;
  onDismissNoisePopup: () => void;
  onNoisePopupWorking: () => void;
  onPauseSoundAnimation: () => void;
  onPlaySoundAnimation: () => void;
  onRetry: () => void;
  onStartSoundAssessment: () => void;
  isProcessing: boolean;
  isRecording: boolean;
  isRetryMode: boolean;
  referenceAudioPath: string;
  showNoisePopup: boolean;
  soundDetectionStatus: ISoundDetectionStatus['status'];
}

/**
 * Custom hook for sound performance evaluation.
 * Records audio from microphone while playing a reference audio clip,
 * compares the recorded audio with the reference using frequency spectrum analysis,
 * and manages retry logic with configurable maximum attempts.
 * Provides state management for recording, processing, success/failure status,
 * and cleanup functions for all audio resources.
 */
export function useSoundPerformanceEvaluation(
  options: UseSoundPerformanceEvaluationOptions = {}
): UseSoundPerformanceEvaluationReturn {
  const {
    browserNotSupportedErrorMessage,
    detectionErrorMessage,
    externalDeviceDetectedGeneric,
    externalDetectionErrorMessage,
    maxAttempts = 3,
    mobileSimilarityThreshold,
    onFailure,
    onSuccess,
    referenceAudioPath = '/audio/tone.mp3',
    similarityThreshold = 0.7,
  } = options;

  const t = useTranslations('soundPerformanceEvaluate');
  const audioErrorGenericMessage = t('audioErrorGenericMessage');

  const animationRef: LottieRef = useRef(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioBufferRef = useRef<AudioBuffer | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const audioContextRef = useRef<AudioContext | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const micStreamRef = useRef<MediaStream | null>(null);
  const externalDeviceDetectedGenericRef = useRef(
    externalDeviceDetectedGeneric
  );
  const externalDetectionErrorMessageRef = useRef(
    externalDetectionErrorMessage
  );
  const onFailureRef = useRef(onFailure);
  const onSuccessRef = useRef(onSuccess);
  const recordedAudioURLRef = useRef<string | null>(null);
  const stopMediaRecorderCleanupRef = useRef<(() => void) | null>(null);
  const timeoutIdsRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const [attemptCount, setAttemptCount] = useState(0);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [buttonEnabled, setButtonEnabled] = useState(true);
  const [externalDevicesDetected, setExternalDevicesDetected] = useState<
    IExternalDevice[]
  >([]);
  const [feedbackMessage, setFeedbackMessage] =
    useState<IFeedbackMessage | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [showNoisePopup, setShowNoisePopup] = useState(false);
  const [soundDetectionStatus, setSoundDetectionStatus] =
    useState<ISoundDetectionStatus['status']>(null);

  useEffect(() => {
    externalDeviceDetectedGenericRef.current = externalDeviceDetectedGeneric;
    externalDetectionErrorMessageRef.current = externalDetectionErrorMessage;
    onFailureRef.current = onFailure;
    onSuccessRef.current = onSuccess;
  }, [
    externalDeviceDetectedGeneric,
    externalDetectionErrorMessage,
    onFailure,
    onSuccess,
  ]);

  /**
   * Checks for external audio devices and sets error state if found
   */
  const checkExternalDevices = async (): Promise<boolean> => {
    try {
      const devices = await detectExternalAudioDevices();

      if (devices.length > 0) {
        setExternalDevicesDetected(devices);

        const firstDevice = devices[0];
        const deviceName = firstDevice?.label || '';

        let errorMessage: string;
        const errorMessageTemplate =
          externalDetectionErrorMessageRef.current || '';
        const genericMessage = externalDeviceDetectedGenericRef.current || '';

        if (deviceName && errorMessageTemplate) {
          errorMessage = errorMessageTemplate.replace(
            '{deviceName}',
            deviceName
          );
        } else if (deviceName && genericMessage) {
          errorMessage = genericMessage.replace('{deviceName}', deviceName);
        } else if (errorMessageTemplate) {
          errorMessage = errorMessageTemplate;
        } else if (genericMessage) {
          errorMessage = genericMessage;
        } else {
          errorMessage = '';
        }

        setFeedbackMessage({
          message: errorMessage,
          type: 'error',
        });
        setButtonEnabled(true);

        return true;
      }

      setExternalDevicesDetected([]);

      return false;
    } catch {
      // If detection fails, don't block the test
      return false;
    }
  };

  /**
   * Effect hook to check for external devices on component mount
   * Also preloads audio for immediate playback
   */
  useEffect(() => {
    checkExternalDevices();

    const preloadAudioOnMount = async () => {
      try {
        const context = await createAndResumeAudioContext();

        if (context) {
          audioBufferRef.current = await preloadAudio(
            referenceAudioPath,
            context
          );
          await context.close();
        }
      } catch {
        // Ignore errors during preload
      }
    };

    preloadAudioOnMount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Plays the sound animation
   */
  const onPlaySoundAnimation = () => {
    if (animationRef.current) {
      animationRef.current.play();
    }
  };

  /**
   * Pauses the sound animation
   */
  const onPauseSoundAnimation = () => {
    if (animationRef.current) {
      animationRef.current.pause();
    }
  };

  /**
   * Performs comprehensive cleanup of all resources
   */
  const cleanupAll = () => {
    clearAllTimeouts(timeoutIdsRef);

    if (stopMediaRecorderCleanupRef.current) {
      stopMediaRecorderCleanupRef.current();
      stopMediaRecorderCleanupRef.current = null;
    }

    cleanupMediaRecorder(mediaRecorderRef, micStreamRef, audioChunksRef);
    cleanupAudioResources(
      audioRef,
      recordedAudioURLRef,
      audioContextRef,
      audioBufferRef
    );
    // Inline animation pause to avoid dependency
    if (animationRef.current) {
      animationRef.current.pause();
    }
  };

  // Store cleanup function in ref so useEffect can access it without dependency
  const cleanupAllRef = useRef(cleanupAll);
  cleanupAllRef.current = cleanupAll;

  /**
   * Handles audio recording and comparison test
   */
  const handleWaveformTest = async () => {
    try {
      setIsProcessing(true);
      setButtonEnabled(false);
      setFeedbackMessage(null);

      if (
        typeof navigator === 'undefined' ||
        !navigator.mediaDevices ||
        !navigator.mediaDevices.getUserMedia
      ) {
        throw new Error(browserNotSupportedErrorMessage);
      }

      if (typeof MediaRecorder === 'undefined') {
        throw new Error(browserNotSupportedErrorMessage);
      }

      if (typeof Audio === 'undefined') {
        throw new Error(browserNotSupportedErrorMessage);
      }

      const audioConstraints: MediaTrackConstraints = {
        autoGainControl: false,
        echoCancellation: false,
        noiseSuppression: false,
      };

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: audioConstraints,
      });
      micStreamRef.current = stream;

      const mimeType = getSupportedMimeType();
      const mediaRecorderOptions: MediaRecorderOptions = {};

      if (mimeType) {
        mediaRecorderOptions.mimeType = mimeType;
      }

      const mediaRecorder = new MediaRecorder(stream, mediaRecorderOptions);

      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      setupMediaRecorderErrorHandler(mediaRecorder);

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      setIsRecording(true);
      setAudioPlaying(true);
      onPlaySoundAnimation();

      startMediaRecorder(mediaRecorder);

      const bufferDelay = isMobileDevice() ? 200 : 50;
      const { promise, timeoutId } = wait(bufferDelay);
      timeoutIdsRef.current.push(timeoutId);

      try {
        await promise;
      } finally {
        const index = timeoutIdsRef.current.indexOf(timeoutId);

        if (index > -1) {
          timeoutIdsRef.current.splice(index, 1);
        }
      }

      await resumeAudioContextIfSuspended(browserNotSupportedErrorMessage);

      await setupAndPlayAudio(
        referenceAudioPath,
        audioContextRef,
        audioBufferRef,
        audioRef,
        audioErrorGenericMessage
      );

      const postPlaybackDelay = isMobileDevice() ? 500 : 200;
      const { promise: postPlaybackPromise, timeoutId: postPlaybackTimeoutId } =
        wait(postPlaybackDelay);
      timeoutIdsRef.current.push(postPlaybackTimeoutId);

      try {
        await postPlaybackPromise;
      } finally {
        const index = timeoutIdsRef.current.indexOf(postPlaybackTimeoutId);

        if (index > -1) {
          timeoutIdsRef.current.splice(index, 1);
        }
      }

      const {
        cleanup: stopMediaRecorderCleanup,
        promise: stopMediaRecorderPromise,
      } = stopMediaRecorder(mediaRecorder);
      stopMediaRecorderCleanupRef.current = stopMediaRecorderCleanup;

      try {
        await stopMediaRecorderPromise;
      } finally {
        stopMediaRecorderCleanupRef.current = null;
      }

      setIsRecording(false);
      setAudioPlaying(false);

      stream.getAudioTracks().forEach((track) => {
        track.stop();
      });

      if (audioChunksRef.current.length === 0) {
        throw new Error(detectionErrorMessage);
      }

      const blob = new Blob(audioChunksRef.current, {
        type: mimeType || 'audio/webm',
      });

      if (blob.size === 0) {
        throw new Error(detectionErrorMessage);
      }

      const file = createRecordedAudioFile(blob, mimeType);
      const comparisonResult = await compareAudioWithMFCC(
        referenceAudioPath,
        file,
        mobileSimilarityThreshold,
        browserNotSupportedErrorMessage,
        similarityThreshold
      );

      if (comparisonResult.isSimilar) {
        setSoundDetectionStatus('SUCCESS');
        setButtonEnabled(false);
        setIsProcessing(false);
        onSuccessRef.current?.();
      } else {
        const { NOISE_DETECTION_SIMILARITY_MULTIPLIER } =
          getWaveformComparisonConstants();
        const minRMSThreshold = isMobileDevice() ? 0.0015 : 0.008;
        const mediumNoiseThreshold = isMobileDevice() ? 0.006 : 0.025;
        const { recordedRMS, similarityScore } = comparisonResult;
        const hasDetectableNoise = recordedRMS > minRMSThreshold;
        const hasHighRMS = recordedRMS >= mediumNoiseThreshold;
        const hasLowSimilarity =
          similarityScore <
          similarityThreshold * NOISE_DETECTION_SIMILARITY_MULTIPLIER;
        const isMediumOrHighNoise =
          !comparisonResult.isSimilar &&
          hasDetectableNoise &&
          (hasHighRMS || (hasLowSimilarity && recordedRMS > minRMSThreshold));

        if (isMediumOrHighNoise) {
          setShowNoisePopup(true);
          setIsProcessing(false);
        } else if (hasDetectableNoise && recordedRMS < mediumNoiseThreshold) {
          const newAttemptCount = attemptCount + 1;
          setAttemptCount(newAttemptCount);

          if (newAttemptCount >= maxAttempts) {
            setSoundDetectionStatus('FAILURE');
            setIsProcessing(false);
            onFailureRef.current?.();
          } else {
            setFeedbackMessage({
              message:
                detectionErrorMessage || 'Waveform mismatch. Please try again.',
              type: 'error',
            });
            setIsProcessing(false);
            setButtonEnabled(true);
            cleanupAll();
          }
        } else {
          const newAttemptCount = attemptCount + 1;
          setAttemptCount(newAttemptCount);

          if (newAttemptCount >= maxAttempts) {
            setSoundDetectionStatus('FAILURE');
            setIsProcessing(false);
            onFailureRef.current?.();
          } else {
            setFeedbackMessage({
              message:
                detectionErrorMessage || 'Waveform mismatch. Please try again.',
              type: 'error',
            });
            setIsProcessing(false);
            setButtonEnabled(true);
            cleanupAll();
          }
        }
      }
    } catch (error) {
      setIsProcessing(false);
      setButtonEnabled(true);
      setSoundDetectionStatus('FAILURE');
      setIsRecording(false);
      setAudioPlaying(false);

      if (stopMediaRecorderCleanupRef.current) {
        stopMediaRecorderCleanupRef.current();
        stopMediaRecorderCleanupRef.current = null;
      }

      if (micStreamRef.current) {
        const tracks = micStreamRef.current.getAudioTracks();

        if (tracks && Array.isArray(tracks)) {
          tracks.forEach((track) => {
            if (
              track &&
              typeof track.stop === 'function' &&
              track.readyState !== 'ended'
            ) {
              track.stop();
            }
          });
        }
      }

      const errorMessage =
        error instanceof Error ? error.message : String(error);
      const isBrowserNotSupported =
        errorMessage.includes('browser') ||
        errorMessage.includes('AudioContext') ||
        errorMessage.includes('not supported');

      if (isBrowserNotSupported && browserNotSupportedErrorMessage) {
        setFeedbackMessage({
          message: browserNotSupportedErrorMessage,
          type: 'error',
        });
      } else if (detectionErrorMessage) {
        setFeedbackMessage({
          message: detectionErrorMessage,
          type: 'error',
        });
      } else {
        setFeedbackMessage({
          message: errorMessage,
          type: 'error',
        });
      }

      logErrorToSentry({
        error: error instanceof Error ? error : new Error(String(error)),
        errorKey: ErrorEventsEnum.ERROR_IN_PERMISSION,
        errorName: 'SOUND TEST FAILED',
        source: 'useSoundPerformanceEvaluation.handleWaveformTest',
      });

      cleanupAll();
      onFailureRef.current?.();
    }
  };

  /**
   * Starts the sound assessment test
   */
  const onStartSoundAssessment = async () => {
    setFeedbackMessage(null);
    setSoundDetectionStatus(null);

    await initializeAudioContextOnInteraction();

    const hasExternalDevices = await checkExternalDevices();

    if (hasExternalDevices) {
      return;
    }

    handleWaveformTest();
  };

  /**
   * Handles retry button click
   */
  const onRetry = async () => {
    setFeedbackMessage(null);

    await initializeAudioContextOnInteraction();

    const hasExternalDevices = await checkExternalDevices();

    if (hasExternalDevices) {
      return;
    }

    handleWaveformTest();
  };

  /**
   * Dismisses the noise popup and resets state for retry
   */
  const onDismissNoisePopup = () => {
    setShowNoisePopup(false);
    setIsProcessing(false);
    setButtonEnabled(true);
    cleanupAll();
  };

  /**
   * Handles noise popup working - marks test as success
   */
  const onNoisePopupWorking = () => {
    setShowNoisePopup(false);
    setSoundDetectionStatus('SUCCESS');
    setButtonEnabled(false);
    setIsProcessing(false);
    onSuccessRef.current?.();
  };

  const isRetryMode = Boolean(
    feedbackMessage &&
      (externalDevicesDetected.length > 0 ||
        (attemptCount > 0 && attemptCount < maxAttempts))
  );

  useEffect(
    () => () => {
      cleanupAllRef.current();
    },
    []
  );

  return {
    animationRef,
    attemptCount,
    audioPlaying,
    audioRef,
    buttonEnabled,
    cleanupAll,
    feedbackMessage,
    onDismissNoisePopup,
    onNoisePopupWorking,
    onPauseSoundAnimation,
    onPlaySoundAnimation,
    onRetry,
    onStartSoundAssessment,
    isProcessing,
    isRecording,
    isRetryMode,
    referenceAudioPath,
    showNoisePopup,
    soundDetectionStatus,
  };
}
